import { FormikProps, withFormik } from 'formik'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useModal } from '@context/ModalsContext'

import { Body1 } from '@ui/Typography'
import { PopoverInput } from '@ui/components/Popover'

import { FormSubmitValues } from '@utils/formValidation'

import { NameDto } from '@model/NameDto'
import { CurrencyType } from '@model/WalletDto'

import { Box } from '@shared/components/Box'
import { RadioModal } from '@shared/modals/Radio.modal'

interface OuterProps {
  currencies: NameDto<CurrencyType>[]
  displayCurrency?: string
  onSubmit(values: FormSubmitValues<DisplayCurrencyFormValues>): Promise<void>
}

export interface DisplayCurrencyFormValues {
  displayCurrencyId: string
}

const DisplayCurrencyFormUI: FC<FormikProps<DisplayCurrencyFormValues> & OuterProps> = (props) => {
  const { currencies, setFieldValue, setSubmitting, handleSubmit, values } = props
  const { t } = useTranslation()

  const currencyOptions = useMemo(() => currencies.map((c) => c.id), [currencies])

  const handleSelectOption = (id: string) => {
    currencyModal.close()
    setFieldValue('displayCurrencyId', id)
    setSubmitting(true)
    handleSubmit()
  }

  const currencyModal = useModal(RadioModal, {
    title: t('Display Currency'),
    onSelect: handleSelectOption,
    initialValue: values.displayCurrencyId,
    simpleOptions: currencyOptions,
  })

  return (
    <Box flex gap='x24' direction='column'>
      <Body1>{t('Choose in what currency you want your total balances to be displayed')}</Body1>
      <PopoverInput
        value={values.displayCurrencyId as CurrencyType}
        onChange={(option) => {
          setFieldValue('displayCurrencyId', option.id)
          setSubmitting(true)
          handleSubmit()
        }}
        options={currencies.map((c) => ({ id: c.id, name: c.id }))}
        label={t('Display Currency')}
      />
    </Box>
  )
}

export const DisplayCurrencyForm = withFormik<OuterProps, DisplayCurrencyFormValues>({
  mapPropsToValues: ({ displayCurrency }) => {
    return {
      displayCurrencyId: displayCurrency || '',
    }
  },
  validate: () => {},
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  enableReinitialize: true,
})(DisplayCurrencyFormUI)
